(() => {
  if (window.TortliCartAddonsInitialized) return;
  window.TortliCartAddonsInitialized = true;

  let detailsDrawer;
  let detailsTrigger;
  let detailsParentContext;
  let editingParentContext;
  let editOpenBypass = false;
  let editSaveBypass = false;
  let orphanCleanupPromise = null;
  let singletonCleanupPromise = null;
  const pendingArtworkStorageKey = 'tortliPendingEditedArtwork';

  const disableExternalCartThumbnailLinks = (root = document) => {
    const selector = '.cart-item__media .tee-cart-thumbnail__link';
    const links = [];

    if (root.nodeType === Node.ELEMENT_NODE && root.matches(selector)) links.push(root);
    if (root.querySelectorAll) links.push(...root.querySelectorAll(selector));

    links.forEach((link) => {
      link.removeAttribute('href');
      link.removeAttribute('target');
      link.removeAttribute('rel');
      link.tabIndex = -1;
    });
  };

  const initializeSamePatternMockups = () => {
    document.querySelectorAll('[data-cart-design-source]:not([data-cart-design-initialized])').forEach((image) => {
      const mockup = image.closest('[data-cart-personalized-mockup]');
      const source = image.dataset.cartDesignSource || '';
      const productionSource = image.dataset.cartProductionSource || '';
      let attempts = 0;

      if (!mockup || !source) return;

      image.dataset.cartDesignInitialized = 'true';

      const showDesign = () => {
        mockup.classList.add('has-design');
      };

      const retryDesign = () => {
        mockup.classList.remove('has-design');
        attempts += 1;
        if (attempts > 20) return;

        window.setTimeout(() => {
          const separator = source.includes('?') ? '&' : '?';
          image.src = `${source}${separator}tortli_mockup_retry=${Date.now()}`;
        }, 30000);
      };

      image.addEventListener('load', showDesign);
      image.addEventListener('error', retryDesign);

      if (image.complete) {
        if (image.naturalWidth > 0) showDesign();
        else retryDesign();
      }

      if (productionSource && productionSource !== source) {
        let productionAttempts = 0;

        const loadProductionDesign = () => {
          productionAttempts += 1;
          if (productionAttempts > 20) return;

          const productionImage = new Image();
          const separator = productionSource.includes('?') ? '&' : '?';

          productionImage.addEventListener('load', () => {
            image.src = productionSource;
            mockup.classList.add('has-design');
          });
          productionImage.addEventListener('error', () => {
            window.setTimeout(loadProductionDesign, 30000);
          });
          productionImage.src = `${productionSource}${separator}tortli_mockup_retry=${Date.now()}`;
        };

        loadProductionDesign();
      }
    });
  };

  const thumbnailLinkObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        disableExternalCartThumbnailLinks(mutation.target);
        return;
      }

      mutation.addedNodes.forEach((node) => disableExternalCartThumbnailLinks(node));
    });

    initializeSamePatternMockups();
  });

  disableExternalCartThumbnailLinks();
  initializeSamePatternMockups();
  const cartObserverRoot = document.querySelector('cart-items, #main-cart-items, main') || document.body;
  thumbnailLinkObserver.observe(cartObserverRoot, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href', 'src'],
  });

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const decodeBase64Url = (value) => {
    const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  };

  const readParentContext = (module) => {
    if (!module) return null;

    let properties = {};
    const propertiesSource = module.querySelector('[data-cart-addon-parent-properties]');

    try {
      const encodedProperties = propertiesSource?.textContent?.trim() || '';
      const parsedProperties = encodedProperties
        ? JSON.parse(decodeBase64Url(encodedProperties))
        : {};
      properties = Array.isArray(parsedProperties)
        ? Object.fromEntries(parsedProperties)
        : parsedProperties;
    } catch (_error) {
      properties = {};
    }

    return {
      module,
      lineKey: module.dataset.parentLineKey || '',
      groupId: module.dataset.groupId || '',
      parentTitle: module.dataset.parentTitle || '',
      parentVariant: module.dataset.parentVariant || '',
      parentProductId: module.dataset.parentProductId || '',
      parentVariantId: module.dataset.parentVariantId || '',
      properties,
    };
  };

  const createGroupId = () => {
    const uniquePart = typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

    return `ZESTAW-${uniquePart.toUpperCase()}`;
  };

  const findProjectReference = (properties) => {
    const projectEntry = Object.entries(properties || {}).find(([key, value]) => {
      if (!value || key === '_Zestaw') return false;
      return /preview|projekt|design|customization/i.test(key);
    });

    return projectEntry ? String(projectEntry[1]) : '';
  };

  const assignGroupToParent = async (context) => {
    if (!context || context.groupId) return context?.groupId || '';

    const groupId = createGroupId();
    const parentProperties = {
      ...context.properties,
      _Zestaw: groupId,
      '_Rola zestawu': 'Produkt bazowy',
      '_Źródło zestawu': 'Koszyk',
      _tortli_group_source: 'cart_page',
    };
    const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: context.lineKey,
        properties: parentProperties,
      }),
    });
    const state = await response.json();

    if (!response.ok || state.status) {
      throw new Error(state.description || (window.tortliAddonStrings?.message_0 || "Could not link the extra to your product."));
    }

    const updatedParent = state.items?.find((item) =>
      item.properties?._Zestaw === groupId && item.properties?._tortli_cart_addon !== 'true'
    );

    context.groupId = groupId;
    context.properties = parentProperties;
    if (updatedParent?.key) context.lineKey = updatedParent.key;
    if (context.module) {
      context.module.dataset.groupId = groupId;
      context.module.dataset.parentLineKey = context.lineKey;
    }

    return groupId;
  };

  const ensureDetailsDrawer = () => {
    if (detailsDrawer) return detailsDrawer;

    const drawer = createElement('div', 'tortli-cart-addon-drawer');
    drawer.hidden = true;

    const backdrop = createElement('button', 'tortli-cart-addon-drawer__backdrop');
    backdrop.type = 'button';
    backdrop.dataset.cartAddonDrawerClose = '';
    backdrop.setAttribute('aria-label', (window.tortliAddonStrings?.message_1 || "Close product details"));

    const panel = createElement('aside', 'tortli-cart-addon-drawer__panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'TortliCartAddonDrawerTitle');

    const header = createElement('div', 'tortli-cart-addon-drawer__header');
    header.append(createElement('p', 'tortli-cart-addon-drawer__eyebrow', (window.tortliAddonStrings?.message_2 || "Extra details")));
    const close = createElement('button', 'tortli-cart-addon-drawer__close', '×');
    close.type = 'button';
    close.dataset.cartAddonDrawerClose = '';
    close.setAttribute('aria-label', (window.tortliAddonStrings?.message_3 || "Close"));
    header.append(close);

    const body = createElement('div', 'tortli-cart-addon-drawer__body');
    const media = createElement('div', 'tortli-cart-addon-drawer__media');
    const image = createElement('img', 'tortli-cart-addon-drawer__image');
    image.alt = '';
    media.append(image);
    const title = createElement('h2', 'tortli-cart-addon-drawer__title');
    title.id = 'TortliCartAddonDrawerTitle';
    const price = createElement('p', 'tortli-cart-addon-drawer__price');
    const description = createElement('div', 'tortli-cart-addon-drawer__description');
    body.append(media, title, price, description);

    const footer = createElement('div', 'tortli-cart-addon-drawer__footer');
    const addButton = createElement('button', 'tortli-cart-addon-drawer__add', (window.tortliAddonStrings?.message_4 || "Add to basket"));
    addButton.type = 'button';
    addButton.dataset.cartAddonButton = '';
    const status = createElement('p', 'tortli-cart-addon-drawer__status');
    status.dataset.cartAddonDrawerStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    footer.append(addButton, status);

    panel.append(header, body, footer);
    drawer.append(backdrop, panel);
    document.body.append(drawer);
    detailsDrawer = drawer;
    return drawer;
  };

  const renderDetailsDrawerMedia = (drawer, trigger) => {
    const media = drawer.querySelector('.tortli-cart-addon-drawer__media');
    const mockupType = trigger.dataset.detailMockupType || '';
    const designSource = trigger.dataset.detailDesignImage || '';
    const fallbackSource = trigger.dataset.detailImage || '';

    media.replaceChildren();
    media.hidden = false;

    if (!fallbackSource && !designSource) {
      media.hidden = true;
      return;
    }

    if ((mockupType === 'mug' || mockupType === 'magic-mug' || mockupType === 'bag' || mockupType === 'pillow' || mockupType === 'mouse-pad') && designSource) {
      const mockup = createElement('span', `tortli-cart-addon-drawer__mockup tortli-cart-addon-drawer__mockup--${mockupType}`);
      const base = createElement('img', 'tortli-cart-addon-drawer__mockup-base');
      base.src = trigger.dataset.detailMockupBase || fallbackSource;
      base.alt = '';

      const design = createElement('img', 'tortli-cart-addon-drawer__mockup-design');
      design.src = designSource;
      design.alt = '';
      design.addEventListener('error', () => {
        media.replaceChildren();
        const fallback = createElement('img', 'tortli-cart-addon-drawer__image');
        fallback.src = fallbackSource;
        fallback.alt = '';
        media.append(fallback);
      }, { once: true });

      mockup.append(base, design);
      media.append(mockup);
      return;
    }

    const image = createElement('img', 'tortli-cart-addon-drawer__image');
    image.src = fallbackSource;
    image.alt = '';
    media.append(image);
  };

  const openDetailsDrawer = (trigger) => {
    const drawer = ensureDetailsDrawer();
    const detailsOnly = trigger.hasAttribute('data-cart-summary-addon-details');
    detailsTrigger = trigger;
    detailsParentContext = readParentContext(trigger.closest('[data-cart-addons]'));
    renderDetailsDrawerMedia(drawer, trigger);
    drawer.querySelector('.tortli-cart-addon-drawer__title').textContent = trigger.dataset.detailTitle || '';
    drawer.querySelector('.tortli-cart-addon-drawer__price').textContent = trigger.dataset.detailPrice || '';
    const description = drawer.querySelector('.tortli-cart-addon-drawer__description');
    const descriptionTemplate = trigger.dataset.detailTemplate
      ? document.querySelector(`template[data-cart-summary-addon-template="${trigger.dataset.detailTemplate}"]`)
      : null;
    description.replaceChildren();
    if (descriptionTemplate) {
      description.append(descriptionTemplate.content.cloneNode(true));
    } else {
      description.textContent = trigger.dataset.detailDescription || '';
    }
    const addButton = drawer.querySelector('[data-cart-addon-button]');
    const drawerFooter = drawer.querySelector('.tortli-cart-addon-drawer__footer');
    addButton.dataset.variantId = trigger.dataset.variantId || '';
    addButton.dataset.parentLineKey = trigger.dataset.parentLineKey || '';
    addButton.disabled = false;
    addButton.removeAttribute('aria-busy');
    addButton.textContent = (window.tortliAddonStrings?.message_4 || "Add to basket");
    drawerFooter.hidden = detailsOnly;
    drawerFooter.style.display = detailsOnly ? 'none' : '';
    drawer.querySelector('[data-cart-addon-drawer-status]').textContent = '';
    drawer.hidden = false;
    document.body.classList.add('tortli-cart-addon-drawer-open');
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      drawer.querySelector('.tortli-cart-addon-drawer__close').focus();
    });
  };

  const closeDetailsDrawer = () => {
    if (!detailsDrawer || detailsDrawer.hidden) return;
    detailsDrawer.classList.remove('is-open');
    document.body.classList.remove('tortli-cart-addon-drawer-open');
    const trigger = detailsTrigger;
    window.setTimeout(() => {
      detailsDrawer.hidden = true;
      trigger?.focus();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250);
  };

  const getSectionTargets = () => [
    {
      id: 'main-cart-items',
      section: document.getElementById('main-cart-items')?.dataset.id || 'main-cart-items',
      selector: '.js-contents',
    },
    {
      id: 'main-cart-footer',
      section: document.getElementById('main-cart-footer')?.dataset.id || 'main-cart-footer',
      selector: '#main-cart-footer',
    },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
  ];

  const replaceSection = (sectionId, html, selector) => {
    if (!html) return;

    const documentParser = new DOMParser().parseFromString(html, 'text/html');
    const targetSection = document.getElementById(sectionId);
    const target = targetSection?.querySelector(selector) || targetSection;
    const source = documentParser.querySelector(selector);

    if (target && source) target.innerHTML = source.innerHTML;
  };

  const setCheckoutGuardPending = (pending) => {
    document.querySelectorAll('[name="checkout"], .cart__checkout-button').forEach((button) => {
      button.disabled = Boolean(pending);
      button.setAttribute('aria-busy', pending ? 'true' : 'false');
    });
  };

  const normalizeSingletonAddons = (cartState) => {
    if (singletonCleanupPromise || !cartState?.items) {
      return singletonCleanupPromise || Promise.resolve(cartState);
    }

    const singletonVariantIds = new Set(
      [...document.querySelectorAll('[data-cart-summary-addon-toggle][data-variant-id]')]
        .map((button) => Number(button.dataset.variantId))
        .filter(Boolean)
    );
    const seenVariants = new Set();
    const singletonUpdates = {};

    cartState.items.forEach((item) => {
      const variantId = Number(item.variant_id);
      if (!singletonVariantIds.has(variantId)) return;

      if (seenVariants.has(variantId)) {
        singletonUpdates[item.key] = 0;
        return;
      }

      seenVariants.add(variantId);
      if (Number(item.quantity) !== 1) singletonUpdates[item.key] = 1;
    });

    if (!Object.keys(singletonUpdates).length) return Promise.resolve(cartState);

    setCheckoutGuardPending(true);
    const sectionTargets = getSectionTargets();
    singletonCleanupPromise = fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: singletonUpdates,
        sections: sectionTargets.map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || (window.tortliAddonStrings?.message_5 || "Could not update the extra quantity."));
        }

        sectionTargets.forEach((section) =>
          replaceSection(section.id, state.sections?.[section.section], section.selector)
        );

        if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'tortli-singleton-addon-cleanup',
            cartData: state,
          });
        }
        return state;
      })
      .catch((error) => {
        console.warn('[Tortli] Nie udało się ograniczyć dodatku do jednej sztuki.', error);
        setCheckoutGuardPending(false);
        return cartState;
      })
      .finally(() => {
        singletonCleanupPromise = null;
      });

    return singletonCleanupPromise;
  };

  const removeOrphanAddons = (cartState) => {
    if (orphanCleanupPromise || !cartState?.items) return orphanCleanupPromise || Promise.resolve(cartState);

    const parentGroups = new Set(
      cartState.items
        .filter((item) => {
          const properties = item.properties || {};
          return properties._Zestaw
            && properties['_Rola zestawu'] === 'Produkt bazowy'
            && properties._tortli_cart_addon !== 'true';
        })
        .map((item) => item.properties._Zestaw)
    );
    const orphanUpdates = {};

    cartState.items.forEach((item) => {
      const properties = item.properties || {};
      const groupId = properties._tortli_group_id || '';
      if (
        properties._tortli_cart_addon === 'true'
        && properties['_Rola zestawu'] === 'Dodatek'
        && groupId
        && !parentGroups.has(groupId)
      ) {
        orphanUpdates[item.key] = 0;
      }
    });

    if (!Object.keys(orphanUpdates).length) {
      setCheckoutGuardPending(false);
      return Promise.resolve(cartState);
    }

    setCheckoutGuardPending(true);
    const sectionTargets = getSectionTargets();
    orphanCleanupPromise = fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: orphanUpdates,
        sections: sectionTargets.map((section) => section.section),
        sections_url: window.location.pathname,
      }),
    })
      .then(async (response) => {
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || (window.tortliAddonStrings?.message_6 || "Could not remove the extra."));
        }

        sectionTargets.forEach((section) =>
          replaceSection(section.id, state.sections?.[section.section], section.selector)
        );

        if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'tortli-orphan-addon-cleanup',
            cartData: state,
          });
        }
        return state;
      })
      .catch((error) => {
        console.warn('[Tortli] Nie udało się usunąć osieroconego dodatku.', error);
        setCheckoutGuardPending(false);
        return cartState;
      })
      .finally(() => {
        orphanCleanupPromise = null;
      });

    return orphanCleanupPromise;
  };

  setCheckoutGuardPending(true);
  fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
  })
    .then((response) => {
      if (!response.ok) throw new Error((window.tortliAddonStrings?.message_7 || "Could not check your basket."));
      return response.json();
    })
    .then(normalizeSingletonAddons)
    .then(removeOrphanAddons)
    .catch((error) => {
      console.warn('[Tortli] Nie udało się sprawdzić powiązań dodatków.', error);
      setCheckoutGuardPending(false);
    });

  if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (
        event?.source === 'tortli-orphan-addon-cleanup'
        || event?.source === 'tortli-singleton-addon-cleanup'
      ) return;
      if (event?.cartData?.items) {
        normalizeSingletonAddons(event.cartData).then(removeOrphanAddons);
      }
    });
  }

  const getArtworkCaptureApi = () =>
    window.TortliTeeArtworkCapture
    || document.documentElement.__tortliArtworkCaptureApi
    || null;

  const getPendingEditedArtwork = () => {
    try {
      const pending = JSON.parse(window.sessionStorage.getItem(pendingArtworkStorageKey) || 'null');
      if (!pending || !pending.groupId || !pending.artworkUrl) return null;
      if (Date.now() - Number(pending.createdAt || 0) > 10 * 60 * 1000) {
        window.sessionStorage.removeItem(pendingArtworkStorageKey);
        return null;
      }
      return pending;
    } catch (_error) {
      return null;
    }
  };

  const setPendingEditedArtwork = (context, artworkUrl) => {
    if (!context?.groupId || !artworkUrl) return;
    try {
      window.sessionStorage.setItem(pendingArtworkStorageKey, JSON.stringify({
        groupId: context.groupId,
        artworkUrl,
        createdAt: Date.now(),
      }));
    } catch (_error) {
      // The cart still saves normally when session storage is unavailable.
    }
  };

  const syncPendingEditedArtwork = async (attempt = 0) => {
    const pending = getPendingEditedArtwork();
    if (!pending) return;

    const visibleEditModal = Array.from(document.querySelectorAll('.vm--modal')).some((modal) => {
      const rect = modal.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });

    if (visibleEditModal) {
      if (attempt < 30) window.setTimeout(() => syncPendingEditedArtwork(attempt + 1), 500);
      return;
    }

    try {
      const cartResponse = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      const cart = await cartResponse.json();
      const parent = cart.items?.find((item) =>
        item.properties?._Zestaw === pending.groupId
        && item.properties?.['_Rola zestawu'] === 'Produkt bazowy'
      );

      if (!parent) throw new Error('Nie znaleziono produktu bazowego zestawu.');

      const sectionTargets = getSectionTargets();
      const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: parent.key,
          quantity: parent.quantity,
          properties: {
            ...parent.properties,
            _tortli_artwork_image: pending.artworkUrl,
            _pc_personalization_preview: pending.artworkUrl,
          },
          sections: sectionTargets.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || (window.tortliAddonStrings?.message_8 || "Could not refresh your design preview."));
      }

      sectionTargets.forEach((section) =>
        replaceSection(section.id, state.sections?.[section.section], section.selector)
      );
      window.sessionStorage.removeItem(pendingArtworkStorageKey);
      initializeSamePatternMockups();

      if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'tortli-cart-personalization-sync',
          cartData: state,
        });
      }
    } catch (_error) {
      if (attempt < 12) window.setTimeout(() => syncPendingEditedArtwork(attempt + 1), 1000);
    }
  };

  const findEditingParentContext = (button) => {
    const cartRow = button.closest('.cart-item');
    const addonsRow = cartRow?.nextElementSibling;
    return readParentContext(addonsRow?.querySelector('[data-cart-addons]'));
  };

  const continueEditAction = (button, type) => {
    if (type === 'open') editOpenBypass = true;
    else editSaveBypass = true;
    button.click();
    window.setTimeout(() => {
      if (type === 'open') editOpenBypass = false;
      else editSaveBypass = false;
    }, 0);
  };

  const captureCheckoutImage = async (button, mockupType, module, drawer) => {
    if (mockupType !== 'mug' && mockupType !== 'magic-mug' && mockupType !== 'bag' && mockupType !== 'pillow' && mockupType !== 'mouse-pad') return '';
    const checkoutApi = window.TortliCheckoutThumbnail;
    if (!checkoutApi?.composeAndUpload) return '';

    const card = module ? button.closest('[data-cart-mockup-type]') : null;
    const baseUrl = card?.querySelector('.tortli-cart-addons__mockup-base')?.src
      || (drawer ? detailsTrigger?.dataset.detailMockupBase : '');
    const designUrl = card?.querySelector('.tortli-cart-addons__mockup-design')?.src
      || (drawer ? detailsTrigger?.dataset.detailDesignImage : '');
    if (!baseUrl || !designUrl) return '';

    return checkoutApi.composeAndUpload({ type: mockupType, baseUrl, designUrl });
  };

  const updateCheckoutImageInBackground = ({ variantId, groupId, mockupType, checkoutImagePromise }) => {
    if ((mockupType !== 'mug' && mockupType !== 'magic-mug' && mockupType !== 'bag' && mockupType !== 'pillow' && mockupType !== 'mouse-pad') || !checkoutImagePromise) return;

    Promise.resolve(checkoutImagePromise)
      .then(async (checkoutImage) => {
        if (!checkoutImage) return;

        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await cartResponse.json();
        if (!cartResponse.ok || cart.status) throw new Error(cart.description || (window.tortliAddonStrings?.message_9 || "Could not load your basket."));

        const addonLine = cart.items?.find((item) =>
          Number(item.variant_id) === Number(variantId)
          && item.properties?._tortli_group_id === groupId
          && item.properties?._tortli_mockup_type === mockupType
        );
        if (
          !addonLine
          || (
            addonLine.properties?._tortli_checkout_image === checkoutImage
            && addonLine.properties?._customization_image === checkoutImage
          )
        ) return;

        const changeResponse = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: addonLine.key,
            quantity: addonLine.quantity,
            properties: {
              ...addonLine.properties,
              _tortli_checkout_image: checkoutImage,
              _customization_image: checkoutImage,
            },
          }),
        });
        const state = await changeResponse.json();
        if (!changeResponse.ok || state.status) {
          throw new Error(state.description || (window.tortliAddonStrings?.message_10 || "Could not save your design preview."));
        }
      })
      .catch((error) => {
        console.warn('[Tortli] Miniatura checkout zostanie pominięta.', error);
      });
  };

  const addAddon = async (button) => {
    const module = button.closest('[data-cart-addons]');
    const drawer = button.closest('.tortli-cart-addon-drawer');
    const parentContext = module ? readParentContext(module) : detailsParentContext;
    const status = module?.querySelector('[data-cart-addon-status]') || drawer?.querySelector('[data-cart-addon-drawer-status]');
    const variantId = Number(button.dataset.variantId);
    const mockupType = module
      ? button.closest('[data-cart-mockup-type]')?.dataset.cartMockupType || ''
      : detailsTrigger?.dataset.detailMockupType || '';

    if (!variantId || button.disabled) return;

    const originalLabel = status?.textContent || '';
    const originalButtonLabel = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = (window.tortliAddonStrings?.message_11 || "Adding to basket\u2026");
    if (drawer) button.textContent = (window.tortliAddonStrings?.message_12 || "Adding\u2026");

    const checkoutImagePromise = captureCheckoutImage(button, mockupType, module, drawer);

    try {
      const groupId = await assignGroupToParent(parentContext);
      const projectReference = findProjectReference(parentContext?.properties);
      const groupSource = parentContext?.properties?._tortli_group_source || 'cart_page';
      const groupSourceLabel = parentContext?.properties?.['_Źródło zestawu'] || 'Koszyk';
      const addonProperties = {
        _tortli_cart_addon: 'true',
        _tortli_group_id: groupId,
        _tortli_group_source: groupSource,
        _tortli_add_source: 'cart_page',
        _tortli_parent_line: parentContext?.lineKey || button.dataset.parentLineKey || '',
        _Zestaw: groupId,
        '_Rola zestawu': 'Dodatek',
        '_Źródło zestawu': groupSourceLabel,
        '_Źródło dodania': 'Koszyk',
        '_Przypisany do': parentContext?.parentTitle || '',
      };

      if (parentContext?.parentVariant && parentContext.parentVariant !== 'Default Title') {
        addonProperties['_Wariant bazowy'] = parentContext.parentVariant;
      }
      if (parentContext?.parentProductId) addonProperties._tortli_parent_product_id = parentContext.parentProductId;
      if (parentContext?.parentVariantId) addonProperties._tortli_parent_variant_id = parentContext.parentVariantId;
      if (projectReference) addonProperties['_Projekt bazowy'] = projectReference;
      if (mockupType === 'mug' || mockupType === 'magic-mug' || mockupType === 'bag' || mockupType === 'pillow' || mockupType === 'mouse-pad') {
        const tibDesignLink = parentContext?.properties?._tib_design_link_1 || '';
        const artworkUrl = parentContext?.properties?._pc_personalization_preview
          || parentContext?.properties?._tortli_artwork_image
          || parentContext?.properties?._tib_design_link_1
          || '';
        const fallbackCustomizationImage = parentContext?.properties?._customization_image
          || parentContext?.properties?._tib_design_link_1
          || parentContext?.properties?._tortli_artwork_image
          || '';
        addonProperties._tortli_mockup_type = mockupType;
        if (tibDesignLink) addonProperties._tib_design_link_1 = tibDesignLink;
        if (artworkUrl) {
          addonProperties._tortli_artwork_image = artworkUrl;
          addonProperties._pc_personalization_preview = artworkUrl;
        }
        if (fallbackCustomizationImage) addonProperties._customization_image = fallbackCustomizationImage;
      }

      const sectionTargets = getSectionTargets();
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: addonProperties,
            },
          ],
          sections: sectionTargets.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });

      const state = await response.json();
      if (!response.ok || state.status) throw new Error(state.description || (window.tortliAddonStrings?.message_13 || "Could not add the item."));

      updateCheckoutImageInBackground({
        variantId,
        groupId,
        mockupType,
        checkoutImagePromise,
      });

      sectionTargets.forEach((section) =>
        replaceSection(section.id, state.sections?.[section.section], section.selector)
      );

      if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'tortli-cart-addons',
          cartData: state,
          variantId,
        });
      }
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (status) status.textContent = error.message || (window.tortliAddonStrings?.message_14 || "Could not add the item. Please try again.");
      button.textContent = originalButtonLabel;
      return;
    }

    if (status) status.textContent = originalLabel;
    if (drawer) closeDetailsDrawer();
  };

  const toggleSummaryAddon = async (button) => {
    if (button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    const lineKey = button.dataset.lineKey || '';
    const isSelected = button.getAttribute('aria-pressed') === 'true';
    const addonKey = button.dataset.addonKey || '';
    const addonRole = button.dataset.addonRole || 'Dodatek';
    const addonSource = button.dataset.addonSource || 'Koszyk - podsumowanie';
    const status = document.querySelector(`[data-cart-summary-addon-status="${addonKey}"]`);
    const sectionTargets = getSectionTargets();

    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = isSelected ? (window.tortliAddonStrings?.message_15 || "Removing extra\u2026") : (window.tortliAddonStrings?.message_16 || "Adding extra\u2026");

    try {
      if (!isSelected) {
        const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cartState = await cartResponse.json();

        if (!cartResponse.ok || cartState.status) {
          throw new Error(cartState.description || (window.tortliAddonStrings?.message_7 || "Could not check your basket."));
        }

        if (cartState.items?.some((item) => Number(item.variant_id) === variantId)) {
          button.classList.add('is-selected');
          button.setAttribute('aria-pressed', 'true');
          button.removeAttribute('aria-busy');
          button.querySelector('em')?.replaceChildren((window.tortliAddonStrings?.message_17 || "Already in your basket"));
          if (status) status.textContent = (window.tortliAddonStrings?.message_18 || "This extra is already in your basket.");
          return;
        }
      }

      const endpoint = isSelected
        ? `${window.routes?.cart_change_url || '/cart/change.js'}`
        : `${window.routes?.cart_add_url || '/cart/add.js'}`;
      const payload = isSelected
        ? { id: lineKey, quantity: 0 }
        : {
            items: [{
              id: variantId,
              quantity: 1,
              properties: {
                _tortli_cart_addon: 'true',
                _tortli_add_source: 'cart_summary',
                '_Rola zestawu': addonRole,
                '_Źródło dodania': addonSource,
              },
            }],
          };

      payload.sections = sectionTargets.map((section) => section.section);
      payload.sections_url = window.location.pathname;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || (window.tortliAddonStrings?.message_19 || "Could not update the extra."));
      }

      sectionTargets.forEach((section) =>
        replaceSection(section.id, state.sections?.[section.section], section.selector)
      );

      if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'tortli-cart-summary-addon',
          cartData: state,
          variantId,
        });
      }
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (status) status.textContent = error.message || (window.tortliAddonStrings?.message_20 || "Could not update the extra. Please try again.");
    }
  };

  const removeDiscounts = async (button) => {
    if (button.disabled) return;

    const sectionTargets = getSectionTargets();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: '',
          sections: sectionTargets.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        throw new Error(state.description || (window.tortliAddonStrings?.message_21 || "Could not remove the discount code."));
      }

      sectionTargets.forEach((section) =>
        replaceSection(section.id, state.sections?.[section.section], section.selector)
      );

      if (window.PUB_SUB_EVENTS?.cartUpdate && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'tortli-cart-discount',
          cartData: state,
        });
      }
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      window.alert(error.message || (window.tortliAddonStrings?.message_22 || "Could not remove the discount code. Please try again."));
    }
  };

  document.addEventListener('click', (event) => {
    const editButton = event.target.closest('.tee-edit-button');
    if (editButton) {
      const context = findEditingParentContext(editButton);
      if (context) editingParentContext = context;

      if (context && !context.groupId && !editOpenBypass) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const wasDisabled = editButton.disabled;
        editButton.disabled = true;

        assignGroupToParent(context)
          .then(() => {
            editingParentContext = context;
            editButton.disabled = wasDisabled;
            continueEditAction(editButton, 'open');
          })
          .catch(() => {
            editButton.disabled = wasDisabled;
            continueEditAction(editButton, 'open');
          });
      }
      return;
    }

    const editSaveButton = event.target.closest('.vm--modal .tee-btn--atc');
    if (editSaveButton && editingParentContext && !editSaveBypass) {
      const context = editingParentContext;
      const captureApi = getArtworkCaptureApi();
      const capture = captureApi && typeof captureApi.capture === 'function'
        ? captureApi.capture()
        : Promise.resolve('');

      capture
        .then((artworkUrl) => {
          if (!artworkUrl) return;
          setPendingEditedArtwork(context, artworkUrl);
          syncPendingEditedArtwork();
        });
    }
  }, true);

  window.setTimeout(() => syncPendingEditedArtwork(), 800);

  document.addEventListener('click', (event) => {
    const discountRemoveButton = event.target.closest('[data-cart-discount-remove]');
    if (discountRemoveButton) {
      event.preventDefault();
      removeDiscounts(discountRemoveButton);
      return;
    }

    const summaryAddonButton = event.target.closest('[data-cart-summary-addon-toggle]');
    if (summaryAddonButton) {
      event.preventDefault();
      toggleSummaryAddon(summaryAddonButton);
      return;
    }

    const detailsButton = event.target.closest('[data-cart-addon-details], [data-cart-summary-addon-details]');
    if (detailsButton) {
      event.preventDefault();
      openDetailsDrawer(detailsButton);
      return;
    }

    if (event.target.closest('[data-cart-addon-drawer-close]')) {
      event.preventDefault();
      closeDetailsDrawer();
      return;
    }

    const button = event.target.closest('[data-cart-addon-button]');
    if (!button) return;

    event.preventDefault();
    addAddon(button);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-cart-discount-form]');
    if (!form) return;

    event.preventDefault();
    const code = new FormData(form).get('discount')?.toString().trim();
    if (!code) return;

    const redirect = window.routes?.cart_url || '/cart';
    window.location.assign(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDetailsDrawer();
  });
})();
